/* global YT */
import fastdom from 'fastdom';
import keyCodes from 'a11y/lib/keyCodes';

export default class VideoPlayer {
  constructor({ element, children, options }) {
    this.element = element;
    this.children = children;
    this.options = options;

    const {
      children: {
        videoContainer,
        videoLink,
      },
      options: {
        playerVars,
      },
    } = this;
    const { videoId } = videoLink.dataset;

    /**
     * The player ID passed to the YouTube API.
     * @type {String}
     */
    this.playerId = videoContainer.id;

    /**
     * Holds an instance of the YouTube player.
     * @type {Object}
     */
    this.player = {};

    /**
     * Hold the iframe element once the player is instantiated.
     * @type {HTMLElement}
     */
    this.iframeElement = null;

    /**
     * Holds video options.
     * @type {Object}
     */
    this.videoOptions = {
      videoId,
      playerVars,
      events: {
        onReady: this.playerReady.bind(this),
      },
    };

    // Bind class methods
    this.insertYouTubeScript = this.insertYouTubeScript.bind(this);
    this.youTubeIframeAPIReady = this.youTubeIframeAPIReady.bind(this);
    this.calcInitialIframeSize = this.calcInitialIframeSize.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.loadVideo = this.loadVideo.bind(this);
    this.killPlayer = this.killPlayer.bind(this);
    this.resizeIframe = this.resizeIframe.bind(this);
    this.registerEventListeners = this.registerEventListeners.bind(this);

    this.registerEventListeners();
  }

  /**
   * Add spacebar support to the video link's click handler.
   *
   * This component adds a button role to the video link. This ensures the
   * "button" behaves as expected.
   *
   * @param  {Object} event The event object.
   */
  handleKeydown(event) {
    const { SPACE } = keyCodes;

    if (SPACE === event.keyCode) {
      event.preventDefault();
      this.loadVideo(event);
    }
  }

  /**
   * Register event listeners for the video player.
   */
  registerEventListeners() {
    const { videoLink, videoIcon } = this.children;

    videoIcon.removeAttribute('data-video-loading');
    videoLink.setAttribute('role', 'button');
    videoLink.addEventListener('keydown', this.handleKeydown);
    videoLink.addEventListener('click', this.loadVideo);
  }

  /**
   * Insert the YT iframe API script tag to the page head.
   *
   * Defines a global variable, player, which refers to the video player and
   * constructs the video player object.
   */
  insertYouTubeScript() {
    const youTubeScriptEl = document.getElementById('yt-iframe-api');
    if (youTubeScriptEl === null) {
      const tag = document.createElement('script');
      tag.id = 'yt-iframe-api';
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    const lastOnReady = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      if (typeof lastOnReady === 'function') {
        lastOnReady();
      }

      this.youTubeIframeAPIReady();
    };
  }

  /**
   * The player API code is downloaded and ready.
   * Add event listeners and DOM attributes.
   */
  youTubeIframeAPIReady() {
    fastdom.measure(this.calcInitialIframeSize);

    fastdom.mutate(() => {
      this.element.classList.add('player-active');

      if (this.player instanceof YT.Player) {
        this.playerReady();
      } else {
        this.player = new YT.Player(this.playerId, this.videoOptions);
        this.playerReady();
      }

      this.iframeElement = document.getElementById(this.playerId);
    });

    window.addEventListener('resize', this.resizeIframe);
  }

  /**
   * Calculate the initial dimensions available to maintain video aspect ratio.
   */
  calcInitialIframeSize() {
    // Base the dimensions on the available width.
    const width = this.element.offsetWidth;
    const height = Math.floor(width * (9 / 16));

    Object.assign(this.videoOptions, { height, width });
  }

  /**
   * Resize the iframe on window resize to maintain the proper width.
   */
  resizeIframe() {
    if (this.iframeElement !== null) {
      fastdom.measure(() => {
        const newWidth = this.element.offsetWidth;

        fastdom.mutate(() => {
          this.iframeElement.setAttribute('height', newWidth * (9 / 16));
          this.iframeElement.setAttribute('width', newWidth);
        });
      });
    }
  }

  /**
   * Load the selected video in the thumbnail space.
   *
   * @param {Object} event The event object.
   */
  loadVideo(event) {
    const { videoId } = this.videoOptions;

    if (videoId) {
      event.preventDefault();

      if (!window.YT?.Player) {
        this.insertYouTubeScript();
      } else {
        this.youTubeIframeAPIReady();
      }
    }
  }

  /**
   * The video player is ready, to play the video.
   *
   * @param  {Object} event The event object.
   */
  playerReady(event) {
    const { height, width } = this.videoOptions;
    const player = (undefined === event) ? YT.get(this.playerId) : event.target;

    player.setSize(width, height);
    if (player.playVideo) {
      player.playVideo();
    }
  }

  /**
   * Destroy the player and restore the hero
   */
  killPlayer() {
    if (this.player instanceof YT.Player) {
      this.player.destroy();
      this.player = {};
      this.element.classList.remove('player-active');
    }
  }
}
